// Security / threat-detection dashboard (client half of the adaptive rate-limiting layer).
// Permission-gated by security:read. Shows realtime traffic, risk levels, top offenders,
// active blocks and a high-risk event feed with attack-flow visualization. Auto-refreshes.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { Card, PageHeader, Button } from '../../ui/primitives.tsx';

type WindowKey = '5m' | '1h' | '24h' | '7d';

interface SecurityEvent {
  id: string;
  event: string;
  level: string;
  score: number;
  endpoint: string;
  method: string;
  ipTruncated: string;
  userId: string | null;
  status: number | null;
  requestCount: number;
  failureCount: number;
  reason: string;
  action: string;
  detail: Record<string, string | number | boolean>;
  timestamp: string;
}

interface BlockRow {
  key: string;
  scope: string;
  reason: string;
  level: string;
  strikes: number;
  until: string;
  createdAt: string;
}

interface Overview {
  generatedAt: string;
  windowMinutes: number;
  store: { kind: string; degraded: boolean };
  enabled: boolean;
  metrics: {
    totalRequests: number;
    requestsPerMinute: number;
    authFailures: number;
    blocked: number;
    byStatusClass: Record<string, number>;
    byLevel: Record<string, number>;
    topIps: Array<{ key: string; count: number }>;
    topEndpoints: Array<{ key: string; count: number }>;
    topAccounts: Array<{ key: string; count: number }>;
    timeline: Array<{ minute: number; total: number; authFailures: number; blocked: number; errors: number }>;
  };
  activeBlocks: number;
  blocks: BlockRow[];
  highRiskEvents: SecurityEvent[];
}

const LEVEL_COLORS: Record<string, string> = {
  LOW: 'text-muted',
  NORMAL: 'text-muted',
  ELEVATED: 'text-amber-600',
  SUSPICIOUS: 'text-amber-600',
  HIGH: 'text-status-urgent-fg',
  CRITICAL: 'text-status-urgent-fg',
};

const WINDOWS: Array<{ key: WindowKey; label: string }> = [
  { key: '5m', label: 'Last 5 min' },
  { key: '1h', label: 'Last hour' },
  { key: '24h', label: '24 hours' },
  { key: '7d', label: '7 days' },
];

export function SecurityDashboard() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [win, setWin] = useState<WindowKey>('1h');
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api
      .get<Overview>(`/security/overview?window=${win}`)
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch(() => setError('Failed to load security overview'));
  }, [win]);

  useEffect(() => {
    load();
    const id = setInterval(load, 10_000);
    return () => clearInterval(id);
  }, [load]);

  const clearBlock = useCallback(
    (key: string) => {
      void api.post('/security/blocks/clear', { key }).then(load).catch(() => setError('Failed to lift block'));
    },
    [load],
  );

  const maxTimeline = useMemo(
    () => Math.max(1, ...(data?.metrics.timeline.map((t) => t.total) ?? [1])),
    [data],
  );

  if (error) return <p className="text-status-urgent-fg">{error}</p>;
  if (!data) return <p className="text-muted">Loading security overview…</p>;

  const m = data.metrics;

  return (
    <div>
      <PageHeader title="Security & Threat Detection" subtitle="Adaptive rate limiting, abuse prevention and live threat monitoring." />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => setWin(w.key)}
            className={`rounded px-3 py-1.5 text-sm ${win === w.key ? 'bg-accent text-white' : 'bg-surface-2 text-muted hover:bg-surface'}`}
          >
            {w.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-muted">
          store: <span className="font-mono">{data.store.kind}</span>
          {data.store.degraded && <span className="ml-1 text-status-urgent-fg">(degraded → local fallback)</span>}
          {!data.enabled && <span className="ml-2 text-status-urgent-fg">DISABLED</span>}
        </span>
      </div>

      {/* Stat row */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Requests / min" value={m.requestsPerMinute} sub={`${m.totalRequests} in window`} />
        <Stat label="Auth failures" value={m.authFailures} tone={m.authFailures > 0 ? 'warn' : undefined} />
        <Stat label="Throttled / blocked" value={m.blocked} tone={m.blocked > 0 ? 'warn' : undefined} />
        <Stat label="Active blocks" value={data.activeBlocks} tone={data.activeBlocks > 0 ? 'urgent' : undefined} />
      </div>

      {/* Timeline sparkline */}
      <Card className="mb-4 p-4">
        <div className="mb-2 text-sm font-medium">Request timeline</div>
        <div className="flex h-24 items-end gap-0.5">
          {m.timeline.slice(-120).map((t, i) => {
            const h = Math.round((t.total / maxTimeline) * 100);
            const danger = t.blocked > 0 || t.errors > t.total / 2;
            return (
              <div
                key={i}
                title={`min ${t.minute}: ${t.total} req, ${t.errors} err, ${t.blocked} blocked`}
                className={`w-full ${danger ? 'bg-status-urgent-fg' : 'bg-accent'} opacity-80`}
                style={{ height: `${Math.max(2, h)}%` }}
              />
            );
          })}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Risk levels + status classes */}
        <Card className="p-4">
          <div className="mb-3 text-sm font-medium">Risk levels</div>
          <div className="space-y-1.5">
            {Object.entries(m.byLevel).map(([lvl, n]) => (
              <Bar key={lvl} label={lvl} value={n} total={m.totalRequests} colorClass={LEVEL_COLORS[lvl] ?? 'text-muted'} />
            ))}
          </div>
          <div className="mb-2 mt-4 text-sm font-medium">Responses</div>
          <div className="flex flex-wrap gap-2 text-xs">
            {Object.entries(m.byStatusClass).map(([k, n]) => (
              <span key={k} className="rounded bg-surface-2 px-2 py-1 font-mono">
                {k}: {n}
              </span>
            ))}
          </div>
        </Card>

        {/* Top offenders */}
        <Card className="p-4">
          <div className="mb-3 text-sm font-medium">Top sources (truncated IP)</div>
          <TopList rows={m.topIps} />
          <div className="mb-2 mt-4 text-sm font-medium">Top endpoints</div>
          <TopList rows={m.topEndpoints} mono />
        </Card>
      </div>

      {/* Active blocks */}
      <Card className="mt-4 overflow-x-auto p-0">
        <div className="border-b border-line px-4 py-3 text-sm font-medium">Active blocks ({data.blocks.length})</div>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-muted">
              <th className="px-4 py-2 font-medium">Scope</th>
              <th className="px-4 py-2 font-medium">Reason</th>
              <th className="px-4 py-2 font-medium">Level</th>
              <th className="px-4 py-2 font-medium">Strikes</th>
              <th className="px-4 py-2 font-medium">Until</th>
              {isSuperAdmin && <th className="px-4 py-2 font-medium"></th>}
            </tr>
          </thead>
          <tbody>
            {data.blocks.length === 0 && (
              <tr>
                <td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-6 text-center text-muted">
                  No active blocks. All clear.
                </td>
              </tr>
            )}
            {data.blocks.map((b) => (
              <tr key={b.key} className="border-b border-line">
                <td className="px-4 py-2 font-mono text-xs">{b.scope}</td>
                <td className="px-4 py-2 text-xs">{b.reason}</td>
                <td className={`px-4 py-2 text-xs font-medium ${LEVEL_COLORS[b.level] ?? ''}`}>{b.level}</td>
                <td className="px-4 py-2 text-xs">{b.strikes}</td>
                <td className="px-4 py-2 font-mono text-xs">{new Date(b.until).toLocaleTimeString()}</td>
                {isSuperAdmin && (
                  <td className="px-4 py-2 text-right">
                    <Button onClick={() => clearBlock(b.key)} className="text-xs">
                      Lift
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* High-risk event feed with attack-flow visualization */}
      <Card className="mt-4 p-4">
        <div className="mb-3 text-sm font-medium">High-risk events</div>
        {data.highRiskEvents.length === 0 && <p className="text-sm text-muted">No high-risk events in the buffer.</p>}
        <div className="space-y-3">
          {data.highRiskEvents.map((e) => (
            <div key={e.id} className="rounded border border-line p-3">
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className={`font-medium ${LEVEL_COLORS[e.level] ?? ''}`}>{e.level}</span>
                <span className="font-mono">{e.event}</span>
                <span className="text-muted">score {e.score}</span>
                <span className="ml-auto text-muted">{new Date(e.timestamp).toLocaleTimeString()}</span>
              </div>
              <AttackFlow e={e} />
              <div className="mt-1 text-xs text-muted">{e.reason}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, sub, tone }: { label: string; value: number; sub?: string; tone?: 'warn' | 'urgent' }) {
  const color = tone === 'urgent' ? 'text-status-urgent-fg' : tone === 'warn' ? 'text-amber-600' : '';
  return (
    <Card className="p-4">
      <div className="text-xs text-muted">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${color}`}>{value.toLocaleString()}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </Card>
  );
}

function Bar({ label, value, total, colorClass }: { label: string; value: number; total: number; colorClass: string }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`w-24 ${colorClass}`}>{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded bg-surface-2">
        <div className="h-full bg-accent" style={{ width: `${pct}%` }} />
      </div>
      <span className="w-12 text-right font-mono text-muted">{value}</span>
    </div>
  );
}

function TopList({ rows, mono }: { rows: Array<{ key: string; count: number }>; mono?: boolean }) {
  if (!rows.length) return <p className="text-xs text-muted">No data.</p>;
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="space-y-1">
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-2 text-xs">
          <span className={`w-48 truncate ${mono ? 'font-mono' : ''}`} title={r.key}>
            {r.key}
          </span>
          <div className="h-1.5 flex-1 overflow-hidden rounded bg-surface-2">
            <div className="h-full bg-accent" style={{ width: `${Math.round((r.count / max) * 100)}%` }} />
          </div>
          <span className="w-10 text-right font-mono text-muted">{r.count}</span>
        </div>
      ))}
    </div>
  );
}

// Renders the "IP → attempts → accounts → failures → risk → action" flow for an event.
function AttackFlow({ e }: { e: SecurityEvent }) {
  const steps: string[] = [`IP ${e.ipTruncated}`];
  const d = e.detail ?? {};
  if (typeof d.distinctAccounts === 'number') steps.push(`${d.distinctAccounts} accounts`);
  if (typeof d.distinctIps === 'number') steps.push(`${d.distinctIps} source IPs`);
  if (e.failureCount) steps.push(`${e.failureCount} failures`);
  if (typeof d.notFound === 'number') steps.push(`${d.notFound} × 404`);
  if (typeof d.forbidden === 'number') steps.push(`${d.forbidden} × 401/403`);
  if (e.requestCount) steps.push(`${e.requestCount} requests`);
  steps.push(`Risk ${e.level}`);
  steps.push(e.action.replace(/_/g, ' ').toLowerCase());
  return (
    <div className="mt-2 flex flex-wrap items-center gap-1 text-xs">
      {steps.map((s, i) => (
        <span key={i} className="flex items-center gap-1">
          <span className="rounded bg-surface-2 px-2 py-0.5">{s}</span>
          {i < steps.length - 1 && <span className="text-muted">→</span>}
        </span>
      ))}
    </div>
  );
}
